import React, { useRef, useSyncExternalStore } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  AlertCircle,
  CheckCircle2,
  Loader2,
  RotateCcw,
} from "lucide-react";
import * as pdfjsLib from "pdfjs-dist/build/pdf";

import { uploadPdf } from "../../Api_Qdrant/Fast_apis";
import { getFastApiErrorMessage } from "../../Api_Qdrant/fastApiError";
import { uploadPdfStore } from "./uploadPdfStore";

import "./UploadPdf.css";
import uploadSvg from "../../assets/icons/upload.svg";

pdfjsLib.GlobalWorkerOptions.workerSrc =
  `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

const MAX_FILE_SIZE_MB = 5;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const MAX_PAGE_COUNT = 50;

/**
 * Get PDF page count locally.
 */
const getPdfPageCount = async (file) => {
  const buffer = await file.arrayBuffer();

  const pdf = await pdfjsLib
    .getDocument({ data: buffer })
    .promise;

  return pdf.numPages;
};

function UploadPdfComponent() {
  const navigate = useNavigate();

  const fileInputRef = useRef(null);

  const {
    file,
    loading,
    message,
    noticeType,
    error,
    uploadFailed,
  } = useSyncExternalStore(
    uploadPdfStore.subscribe,
    uploadPdfStore.getSnapshot
  );

  /**
   * Open the native file picker. Used by the drop area and by the
   * primary button while no file is selected yet.
   */
  const openFilePicker = () => {
    if (loading) return;

    fileInputRef.current?.click();
  };

  /**
   * When user selects a new file,
   * reset previous upload failure.
   */
  const handleFileChange = (e) => {
    const selected = e.target.files?.[0] || null;

    // Ignore the "cancel" case so an already-picked file isn't wiped.
    if (!selected) return;

    uploadPdfStore.setState({
      file: selected,
      message: "",
      error: "",
      uploadFailed: false,
      noticeType: "",
    });
  };

  const handleUpload = async () => {
    // ========================================================
    // FILE VALIDATION
    // ========================================================

    if (!file) {
      const userMessage =
        "Please select a PDF file first.";

      toast.warning(userMessage);

      uploadPdfStore.setState({
        error: userMessage,
        uploadFailed: false,
        noticeType: "error",
      });

      return;
    }

    if (file.type !== "application/pdf") {
      const userMessage =
        "Only PDF files are supported.";

      toast.error(userMessage);

      uploadPdfStore.setState({
        error: userMessage,
        uploadFailed: false,
        noticeType: "error",
      });

      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      const userMessage =
        `File size exceeds ${MAX_FILE_SIZE_MB}MB limit. ` +
        "Please upload a smaller file.";

      toast.error(userMessage);

      uploadPdfStore.setState({
        error: userMessage,
        uploadFailed: false,
        noticeType: "error",
      });

      return;
    }

    // ========================================================
    // CHECK PDF
    // ========================================================

    uploadPdfStore.setState({
      loading: true,
      message: "Checking PDF...",
      noticeType: "info",
      error: "",
      uploadFailed: false,
    });

    let pageCount;

    try {
      pageCount = await getPdfPageCount(file);
    } catch (err) {
      // Developer can still see the actual error.
      console.error(
        "Failed to read PDF page count:",
        err
      );

      const userMessage =
        "Could not read the PDF file. " +
        "Please make sure it is a valid or unencrypted PDF.";

      uploadPdfStore.setState({
        loading: false,
        message: "",
        noticeType: "error",
        error: userMessage,
        uploadFailed: false,
      });

      toast.error(
        "Could not read the PDF file."
      );

      return;
    }

    // ========================================================
    // PAGE COUNT VALIDATION
    // ========================================================

    if (pageCount > MAX_PAGE_COUNT) {
      const userMessage =
        `PDF has ${pageCount} pages. ` +
        `Please upload a PDF with ${MAX_PAGE_COUNT} pages or fewer.`;

      uploadPdfStore.setState({
        loading: false,
        message: "",
        noticeType: "error",
        error: userMessage,
        uploadFailed: false,
      });

      toast.error(
        "File page count is too large."
      );

      return;
    }

    // ========================================================
    // START UPLOAD
    // ========================================================

    uploadPdfStore.setState({
      loading: true,
      message: "Uploading your PDF document...",
      noticeType: "info",
      error: "",
      uploadFailed: false,
    });

    try {
      const res = await uploadPdf(file);

      // ======================================================
      // NO RESPONSE
      // ======================================================

      if (!res) {
        const userMessage =
          "Unable to connect to the server. " +
          "Please try again.";

        uploadPdfStore.setState({
          message: "",
          noticeType: "error",
          error: userMessage,
          uploadFailed: true,
        });

        toast.error(
          "Connection failed. Please try again."
        );

        return;
      }

      // ======================================================
      // RATE LIMITED
      //
      // Your API interceptor may already display the toast.
      // ======================================================

      if (res?.rateLimited) {
        uploadPdfStore.setState({
          message: "",
          noticeType: "",
          error: "",
          uploadFailed: false,
        });

        return;
      }

      // ======================================================
      // BACKEND FAILURE
      //
      // NEVER display res.message / res.error directly.
      // ======================================================

      const hasBackendError =
        res?.success === false ||
        Boolean(res?.error_code) ||
        Boolean(res?.error) ||
        Boolean(res?.error_value) ||
        Number(res?.status_code) >= 400;

      if (hasBackendError) {
        const userMessage =
          getFastApiErrorMessage(
            res,
            "Could not process the PDF. Please try again."
          );

        uploadPdfStore.setState({
          message: "",
          noticeType: "error",
          error: userMessage,
          uploadFailed: true,
        });

        toast.error(userMessage);

        return;
      }

      // ======================================================
      // SUCCESS
      // ======================================================

      // Clear the picked file so the form resets for the next upload
      // and the same file can't be submitted twice by accident.
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      uploadPdfStore.setState({
        file: null,
        message: "PDF uploaded successfully.",
        noticeType: "success",
        error: "",
        uploadFailed: false,
      });

      toast.success(
        "PDF uploaded successfully."
      );
    } catch (err) {
      // IMPORTANT:
      // Actual backend error stays only in developer console.
      console.error("Upload error:", err);

      // NEVER do:
      //
      // err.response.data.message
      // err.response.data.error
      // err.message
      //
      // for user-facing output.

      const userMessage =
        getFastApiErrorMessage(
          err,
          "Something went wrong during upload. Please try again."
        );

      uploadPdfStore.setState({
        error: userMessage,
        message: "",
        noticeType: "error",
        uploadFailed: true,
      });

      toast.error(userMessage);
    } finally {
      uploadPdfStore.setState({
        loading: false,
      });
    }
  };

  /**
   * Reset failed state and move user to Documents page
   * where the retry functionality is available.
   */
  const handleRetryRedirect = () => {
    uploadPdfStore.setState({
      error: "",
      uploadFailed: false,
      message: "",
      noticeType: "",
    });

    navigate("/documents");
  };

  return (
    <div className="upload-pdf-container upload-pdf-page">
      <section className="upload-hero">
        <div className="upload-hero-copy">
          <p className="upload-eyebrow">
            Upload PDF
          </p>

          <h1>
            Process a document
          </h1>

          <p className="upload-subtitle">
            Upload a PDF to train the Chatbot.
          </p>
        </div>

        <div className="upload-hero-stats">
          <div className="hero-stat-card">
            <span>
              Selected File
            </span>

            <strong>
              {file
                ? file.name
                : "None"}
            </strong>
          </div>

          <div className="hero-stat-card">
            <span>
              Status
            </span>

            <strong>
              {loading
                ? "Uploading"
                : uploadFailed
                  ? "Failed"
                  : error
                    ? "Needs attention"
                    : "Ready"}
            </strong>
          </div>
        </div>
      </section>

      <div className="upload-grid">
        <section className="upload-card">
          <div className="card-header">
            <div>
              <h2>
                Choose PDF
              </h2>

              <p>
                Select a document and upload it
                to the processing service.
              </p>
            </div>
          </div>

          <div
            className={`file-upload-area ${
              loading
                ? "file-upload-area--disabled"
                : ""
            } ${
              file
                ? "file-upload-area--has-file"
                : ""
            }`}
          >
            <img
              src={uploadSvg}
              alt=""
              className="upload-img"
            />

            <div className="file-upload-txt">
              {file ? (
                <>
                  <h3>
                    File selected
                  </h3>

                  <label className="file-input-label">
                    {file.name}
                  </label>

                  <p className="file-upload-hint">
                    Click here to pick a different PDF.
                  </p>
                </>
              ) : (
                <>
                  <h3>
                    Drag &amp; drop your PDF here
                  </h3>

                  <p>
                    or click anywhere in this box to
                    browse your files
                  </p>

                  <p className="file-upload-hint">
                    Maximum size: {MAX_FILE_SIZE_MB}MB
                    {" · "}up to {MAX_PAGE_COUNT} pages
                  </p>
                </>
              )}
            </div>

            <input
              type="file"
              accept="application/pdf"
              className="file-input"
              onChange={handleFileChange}
              ref={fileInputRef}
              id="pdf-upload"
              disabled={loading}
            />
          </div>

          <div className="upload-btn-wraper">
            {uploadFailed ? (
              <div className="upload-info-box upload-info-box--error">
                <AlertCircle size={18} />

                <div className="upload-info-box-text">
                  <strong>
                    Upload incomplete
                  </strong>

                  <span>
                    Something went wrong while
                    processing this PDF. Please
                    retry from the Documents tab.
                  </span>
                </div>

                <button
                  type="button"
                  className="upload-info-retry-btn"
                  onClick={handleRetryRedirect}
                >
                  <RotateCcw size={14} />

                  Retry from Documents tab
                </button>
              </div>
            ) : file ? (
              <div className="upload-actions">
                <button
                  className="primary_btn"
                  onClick={handleUpload}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2
                        className="btn-spinner"
                        size={18}
                      />

                      {message ||
                        "Uploading..."}
                    </>
                  ) : (
                    "Upload PDF"
                  )}
                </button>

                {!loading && (
                  <button
                    type="button"
                    className="upload-change-btn"
                    onClick={openFilePicker}
                  >
                    Change file
                  </button>
                )}
              </div>
            ) : (
              <button
                type="button"
                className="primary_btn"
                onClick={openFilePicker}
                disabled={loading}
              >
                Choose PDF file
              </button>
            )}
          </div>

          {message && (
            <div
              className={`message-box ${noticeType}`}
            >
              {noticeType === "info" ? (
                <Loader2
                  className="btn-spinner"
                  size={16}
                />
              ) : noticeType === "success" ? (
                <CheckCircle2 size={16} />
              ) : (
                <AlertCircle size={16} />
              )}

              <span>
                {message}
              </span>
            </div>
          )}

          {error && !uploadFailed && (
            <div className="message-box error">
              <AlertCircle size={16} />

              <span>
                {error}
              </span>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

export default UploadPdfComponent;

